import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, MessageSquare, FileSearch, Send, Server, Mail, Phone, CreditCard, Scale, FileText, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO, STRUCTURED_DATA } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pricingTiers = [
  {
    name: 'Essentiel',
    subtitle: 'Landing, vitrine simple',
    priceRange: '1 500 – 3 000',
    description: 'Idéal pour présenter une offre, un produit ou capter des leads.',
    features: [
      'Design sur-mesure',
      'Code propre et performant',
      'SEO avancé',
      'Animations soignées',
      'Suivi post-livraison',
    ],
    highlighted: false,
    cta: 'Me contacter',
  },
  {
    name: 'Professionnel',
    subtitle: 'Vitrine complexe, e-commerce',
    priceRange: '3 500 – 12 000',
    description: 'La solution complète pour les professionnels et PME.',
    features: [
      'Design sur-mesure',
      'Animations & interactions',
      'Multi-pages ou e-commerce complet',
      'Blog / espace contenu',
      'Multilingue',
      'Intégrations API (paiement, CRM...)',
      'SEO avancé',
      'Formation utilisation',
      'Suivi post-livraison',
    ],
    highlighted: true,
    cta: 'Me contacter',
  },
  {
    name: 'Institutionnel',
    subtitle: 'Collectivités, établissements publics',
    priceRange: 'Sur devis',
    description: 'Architecture sur-mesure adaptée à votre structure.',
    features: [
      'Conformité RGAA niveau AA (obligation légale EPL)',
      'Conformité RGPD avec coordination DPO',
      'Mentions légales et politique de confidentialité rédigées par juriste',
      'Hébergement géré et maintenance incluse',
      'Adaptation aux procédures de marchés publics',
      'Accompagnement de A à Z',
      'Suivi post-livraison long terme',
    ],
    highlighted: false,
    cta: 'Me contacter',
  },
];

const hostingPlans = [
  {
    name: 'Hébergement',
    price: '300',
    monthlyPrice: '25',
    period: '/an',
    tagline: 'Tarif moyen pour un site vitrine',
    description: 'L\'essentiel pour garder votre site vitrine en ligne. Maintenance incluse, vous n\'avez rien à gérer !',
    features: [
      'Hébergement sécurisé',
      'Certificat SSL inclus',
      'Sauvegardes régulières',
      'Maintenance serveur continue',
      'Support technique réactif',
    ],
    icon: Server,
    highlighted: true,
    badge: 'Le plus courant',
    onQuote: false,
  },
  {
    name: 'Hébergement sur devis',
    price: null,
    monthlyPrice: null,
    period: null,
    tagline: 'Sites institutionnels, apps web…',
    description: 'Pour les projets institutionnels, sites vitrines avancés ou applications web. Tarif et prestations adaptés à vos besoins.',
    features: [
      'Infrastructure dédiée si besoin',
      'Supervision et sauvegardes étendues',
      'SLA et garanties spécifiques',
      'Boîtes mails professionnelles',
      'Maintenance évolutive incluse',
    ],
    icon: Mail,
    highlighted: false,
    onQuote: true,
  },
];

export default function TarifsPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hostingMode, setHostingMode] = useState('axeris'); // 'axeris' or 'self'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.status === 201) {
        toast.success('Message envoyé ! Je vous recontacte rapidement.');
        setFormData({ name: '', email: '', phone: '', company: '', projectType: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="tarifs-page" className="pt-32">
      <SEO 
        title="Tarifs création de site web"
        description="Tarifs transparents pour la création de sites web : site vitrine dès 600€, e-commerce dès 1500€, application web sur devis. Paiement en 3x sans frais. Devis gratuit sous 48h."
        path="/tarifs"
        structuredData={STRUCTURED_DATA.service('Création de site web', 'Création de sites web professionnels sur-mesure', '600')}
      />
      
      {/* Header */}
      <section className="container-main mb-20">
        <div className="max-w-3xl animate-fade-up">
          <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
            Tarifs
          </span>
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-none mb-6">
            Des prix <span className="text-gradient">transparents</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Chaque projet est unique, les prix ci-dessous sont des fourchettes indicatives. 
            Le tarif final dépend de vos besoins spécifiques.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-muted/30 section-padding" data-testid="pricing-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Grille tarifaire</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Des tarifs adaptés à chaque besoin. Prix nets, non assujetti à la TVA. Sur devis selon la complexité du projet.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-start">
            {pricingTiers.map((tier, index) => (
              <Card
                key={tier.name}
                data-testid={`pricing-tier-${index}`}
                className={`relative overflow-hidden flex flex-col ${
                  tier.highlighted
                    ? 'border-2 border-primary shadow-lg shadow-primary/10'
                    : 'border-2 border-transparent hover:border-primary/20'
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-brand" />
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl">{tier.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">{tier.subtitle}</p>
                  {tier.highlighted && (
                    <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground">
                      Le plus populaire
                    </Badge>
                  )}
                </CardHeader>
                <CardContent className="flex flex-col flex-1">
                  <div className="mb-6">
                    <span className="text-4xl font-black">{tier.priceRange}</span>
                    {tier.priceRange !== 'Sur devis' && <span className="text-muted-foreground ml-1">€</span>}
                  </div>
                  <p className="text-muted-foreground mb-6">{tier.description}</p>
                  
                  <ul className="space-y-3 mb-8 flex-1">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a href="#contact" className="mt-auto">
                    <Button
                      data-testid={`pricing-cta-${index}`}
                      className={`w-full ${tier.highlighted ? 'btn-primary' : 'btn-secondary'}`}
                    >
                      {tier.cta}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Payment Options - Full Width */}
          <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-primary/10 via-secondary/5 to-primary/10 border border-primary/20">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              {/* 3x Payment Highlight */}
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-primary/20 shrink-0">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Paiement en 3x sans frais</h3>
                  <p className="text-sm text-muted-foreground">Étalez le coût de votre site sur 3 mois, sans frais supplémentaires</p>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Check className="h-4 w-4 text-primary" />
                  Carte
                </span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Check className="h-4 w-4 text-primary" />
                  Virement
                </span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Check className="h-4 w-4 text-primary" />
                  SEPA
                </span>
                <span className="text-muted-foreground">•</span>
                <a href="#contact" className="text-primary hover:underline font-medium">
                  Autre ? Contactez-moi
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Services Section */}
      <section className="section-padding" data-testid="legal-section">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Vos documents juridiques, rédigés par un juriste</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Titulaire d'une licence en droit, je rédige moi-même les documents juridiques de votre site. 
                Pas de générateurs automatiques, pas de copier-coller : du sur-mesure, adapté à votre activité.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 rounded-xl border-2 border-emerald-500/30 bg-emerald-500/5">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="h-4 w-4 text-emerald-500" />
                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-wide">Inclus</span>
                </div>
                <h3 className="font-bold mb-1">Mentions légales</h3>
                <p className="text-xs text-muted-foreground">Obligatoires, rédigées selon votre structure juridique</p>
              </div>

              <div className="p-5 rounded-xl border-2 border-emerald-500/30 bg-emerald-500/5">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="h-4 w-4 text-emerald-500" />
                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-wide">Inclus</span>
                </div>
                <h3 className="font-bold mb-1">Politique de confidentialité</h3>
                <p className="text-xs text-muted-foreground">Conforme RGPD, adaptée à votre site</p>
              </div>

              <div className="p-5 rounded-xl border border-border bg-card">
                <div className="mb-3">
                  <span className="text-sm font-black text-primary">250 – 500 €</span>
                </div>
                <h3 className="font-bold mb-1">CGV</h3>
                <p className="text-xs text-muted-foreground">Indispensable pour l'e-commerce et les prestations de service</p>
              </div>

              <div className="p-5 rounded-xl border border-border bg-card">
                <div className="mb-3">
                  <span className="text-sm font-black text-primary">200 – 400 €</span>
                </div>
                <h3 className="font-bold mb-1">CGU</h3>
                <p className="text-xs text-muted-foreground">Pour les plateformes, applications et espaces membres</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hosting Plans Section */}
      <section className="bg-muted/30 section-padding" data-testid="hosting-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Gestion & Hébergement</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              Après la création de votre site vitrine, choisissez l'option qui vous convient. <span className="block mt-1 text-sm">Les applications web, gros projets et projets institutionnels font l'objet d'une prestation et de tarifs dédiés.</span>
            </p>

            {/* Toggle Switch */}
            <div className="inline-flex items-center p-1 rounded-full bg-muted border border-border">
              <button
                onClick={() => setHostingMode('axeris')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all ${
                  hostingMode === 'axeris'
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Hébergé par Axeris
              </button>
              <button
                onClick={() => setHostingMode('self')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all ${
                  hostingMode === 'self'
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Auto-hébergement
              </button>
            </div>
          </div>

          {/* Axeris Hosting Plans */}
          {hostingMode === 'axeris' && (
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto animate-fade-up">
              {hostingPlans.map((plan) => {
                const IconComponent = plan.icon;
                return (
                  <Card
                    key={plan.name}
                    data-testid={`hosting-plan-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`relative overflow-hidden flex flex-col ${
                      plan.highlighted
                        ? 'border-2 border-primary shadow-lg shadow-primary/10'
                        : 'border-2 border-transparent hover:border-primary/20'
                    }`}
                  >
                    {plan.highlighted && (
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-brand" />
                    )}
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <IconComponent className="h-6 w-6 text-primary" />
                        </div>
                        {plan.highlighted && (
                          <Badge className="bg-primary text-primary-foreground">{plan.badge || 'Recommandé'}</Badge>
                        )}
                      </div>
                      <CardTitle className="text-2xl">{plan.name}</CardTitle>
                      {plan.tagline && (
                        <p className="text-xs uppercase tracking-wide text-primary font-semibold mt-1">{plan.tagline}</p>
                      )}
                      <p className="text-sm text-muted-foreground min-h-[3rem]">{plan.description}</p>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <div className="mb-6">
                        {plan.onQuote ? (
                          <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-black">Sur devis</span>
                          </div>
                        ) : (
                          <>
                            <span className="text-4xl font-black">{plan.monthlyPrice}</span>
                            <span className="text-muted-foreground ml-1">€/mois</span>
                          </>
                        )}
                      </div>
                      
                      <ul className="space-y-3 flex-1">
                        {plan.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-3">
                            <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Self-hosting Section */}
          {hostingMode === 'self' && (
            <div className="max-w-5xl mx-auto animate-fade-up">
              <Card className="border-2 overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-2">
                    {/* Left - Explanation */}
                    <div className="p-8 md:p-10 flex flex-col">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 rounded-xl bg-muted">
                          <Server className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold">Vous hébergez vous-même</h3>
                          <p className="text-muted-foreground">Gardez le contrôle total</p>
                        </div>
                      </div>
                      
                      <div className="space-y-4 text-muted-foreground flex-1">
                        <p>
                          <strong className="text-foreground">Vous avez votre propre serveur ?</strong><br />
                          Je vous livre le site clé en main, prêt à être déployé sur votre infrastructure.
                        </p>
                        <p>
                          <strong className="text-foreground">Installation par mes soins : 249€</strong><br />
                          Je configure tout sur votre VPS : serveur web, base de données, SSL, etc.
                        </p>
                        <p>
                          <strong className="text-foreground">Ou faites-le vous-même : 0€</strong><br />
                          Je vous envoie le dossier complet avec la documentation d'installation.
                        </p>
                      </div>

                      <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mt-8">
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                          <strong>À noter :</strong> Cette option nécessite des compétences techniques ou l'achat d'une maintenance externe.
                        </p>
                      </div>
                    </div>

                    {/* Right - Cost Summary */}
                    <div className="bg-muted/50 p-8 md:p-10 border-t lg:border-t-0 lg:border-l border-border">
                      <h4 className="text-lg font-bold mb-6">Récapitulatif des coûts</h4>
                      
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-4 rounded-xl bg-background border border-border">
                          <div>
                            <div className="font-semibold">Installation</div>
                            <div className="text-sm text-muted-foreground">Par mes soins, une seule fois</div>
                          </div>
                          <div className="text-2xl font-black">249€</div>
                        </div>

                        <div className="flex justify-between items-center p-4 rounded-xl bg-background border border-border">
                          <div>
                            <div className="font-semibold">Location VPS</div>
                            <div className="text-sm text-muted-foreground">Hostinger, Scaleway...</div>
                          </div>
                          <div className="text-2xl font-black text-muted-foreground">~60-100€<span className="text-sm font-normal">/an</span></div>
                        </div>

                        <div className="flex justify-between items-center p-4 rounded-xl bg-background border border-border">
                          <div>
                            <div className="font-semibold">Maintenance</div>
                            <div className="text-sm text-muted-foreground">Si vous ne gérez pas vous-même</div>
                          </div>
                          <div className="text-2xl font-black text-muted-foreground">~200-500€<span className="text-sm font-normal">/an</span></div>
                        </div>

                        <div className="mt-6 pt-6 border-t border-border space-y-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-bold">Coût 1ère année</div>
                              <div className="text-sm text-muted-foreground">Sans maintenance</div>
                            </div>
                            <div className="text-xl font-black">~310-350€</div>
                          </div>
                          <p className="text-sm text-muted-foreground pt-2">
                            Puis ~60-100€/an pour le VPS uniquement et ~260-600€/an avec la maintenance.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>



      <section id="contact" className="section-padding bg-muted/30" data-testid="contact-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Contact Info */}
            <div className="animate-fade-up">
              <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
                Contact
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Parlons de votre <span className="text-gradient">projet</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Remplissez le formulaire et je vous recontacte sous 48h ouvrées pour discuter de votre projet 
                et vous proposer un devis personnalisé.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-background">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold">Réponse rapide</div>
                    <div className="text-sm text-muted-foreground">Sous 48h ouvrées maximum</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-background">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <FileSearch className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold">Devis gratuit</div>
                    <div className="text-sm text-muted-foreground">Sans engagement, détaillé et transparent</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-background">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold">Échange personnalisé</div>
                    <div className="text-sm text-muted-foreground">Appel ou visio pour bien comprendre vos besoins</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-background">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold">Accompagnement complet</div>
                    <div className="text-sm text-muted-foreground">De l'idée à la mise en ligne, je vous guide</div>
                  </div>
                </div>
                <a 
                  href="mailto:contact@axeris-labs.fr" 
                  className="flex items-center gap-4 p-4 rounded-xl bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold">contact@axeris-labs.fr</div>
                    <div className="text-sm text-muted-foreground">Écrivez-moi directement</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="border-2 animate-fade-up animation-delay-200">
              <CardContent className="p-8">
                {/* Preference note */}
                <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/20 mb-6">
                  <Phone className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">J'aime bien échanger au téléphone ou en face à face si possible.</strong> On se comprend mieux comme ça !
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Nom *</label>
                      <Input
                        data-testid="contact-name"
                        placeholder="Votre nom"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="h-14 bg-muted/50 border-transparent focus:border-primary rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Téléphone *</label>
                      <Input
                        data-testid="contact-phone"
                        type="tel"
                        placeholder="06 12 34 56 78"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="h-14 bg-muted/50 border-transparent focus:border-primary rounded-xl"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <Input
                      data-testid="contact-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-14 bg-muted/50 border-transparent focus:border-primary rounded-xl"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Entreprise</label>
                      <Input
                        data-testid="contact-company"
                        placeholder="Nom de votre entreprise"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="h-14 bg-muted/50 border-transparent focus:border-primary rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Type de projet</label>
                      <Input
                        data-testid="contact-project-type"
                        placeholder="Site vitrine, e-commerce..."
                        value={formData.projectType}
                        onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                        className="h-14 bg-muted/50 border-transparent focus:border-primary rounded-xl"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Votre message *</label>
                    <Textarea
                      data-testid="contact-message"
                      placeholder="Décrivez votre projet, vos objectifs, votre budget approximatif..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="bg-muted/50 border-transparent focus:border-primary rounded-xl resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary text-lg py-6"
                  >
                    {isSubmitting ? (
                      'Envoi en cours...'
                    ) : (
                      <>
                        Envoyer ma demande
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
}
